import { createFileRoute } from "@tanstack/react-router";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useMemo, useRef, useState, useEffect } from "react";
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  flexRender,
  type SortingState,
  type ColumnFiltersState,
  type ColumnDef,
} from "@tanstack/react-table";
import { useVirtualizer } from "@tanstack/react-virtual";
import { apiGet } from "../lib/api";
import { formatCurrency, formatDateFr } from "../lib/format";
import { useDateStore } from "../stores/date-store";
import { OriginBadge, type EntryOrigin } from "../components/entry-origin";

export const Route = createFileRoute("/entries")({
  component: EntriesPage,
});

interface JournalEntry {
  id: number;
  journalCode: string;
  journalLabel: string;
  entryNumber: number;
  entryDate: string;
  accountCode: string;
  accountLabel: string;
  pieceReference: string | null;
  entryLabel: string;
  debit: number;
  credit: number;
  insuranceId: number | null;
  origin: EntryOrigin | null;
  isManualOverride: boolean;
  sourceEntryNumber: string | null;
}

interface EntriesResponse {
  entries: JournalEntry[];
  total: number;
  page: number;
  perPage: number;
}

const PER_PAGE = 500;

const columns: ColumnDef<JournalEntry>[] = [
  {
    accessorKey: "entryDate",
    header: "Date",
    cell: ({ row }) => (
      <span className="whitespace-nowrap text-gray-900">{row.original.entryDate}</span>
    ),
    sortingFn: "datetime",
  },
  {
    accessorKey: "journalCode",
    header: "Journal",
    cell: ({ row }) => (
      <span className="px-2 py-1 rounded bg-gray-100 text-gray-700 font-mono text-xs">
        {row.original.journalCode}
      </span>
    ),
  },
  {
    accessorKey: "entryNumber",
    header: "N°",
    cell: ({ row }) => (
      <span className="text-gray-500">{row.original.entryNumber}</span>
    ),
  },
  {
    accessorKey: "accountCode",
    header: "Compte",
    cell: ({ row }) => (
      <div className="whitespace-nowrap">
        <span className="font-mono text-gray-900">{row.original.accountCode}</span>
        <span className="text-gray-500 ml-2">{row.original.accountLabel}</span>
      </div>
    ),
    sortingFn: "alphanumeric",
  },
  {
    accessorKey: "entryLabel",
    header: "Libellé",
    cell: ({ row }) => (
      <span className="text-gray-500 max-w-xs truncate block">
        {row.original.entryLabel}
      </span>
    ),
  },
  {
    accessorKey: "pieceReference",
    header: "Référence",
    cell: ({ row }) => (
      <span className="text-gray-400 font-mono text-xs">
        {row.original.pieceReference || "-"}
      </span>
    ),
  },
  {
    id: "origin",
    header: "Origine",
    enableSorting: false,
    cell: ({ row }) => <OriginBadge entry={row.original} />,
  },
  {
    accessorKey: "debit",
    header: "Débit",
    cell: ({ row }) => (
      <span className="text-right text-gray-900 tabular-nums">
        {formatCurrency(row.original.debit) || "-"}
      </span>
    ),
  },
  {
    accessorKey: "credit",
    header: "Crédit",
    cell: ({ row }) => (
      <span className="text-right text-gray-900 tabular-nums">
        {formatCurrency(row.original.credit) || "-"}
      </span>
    ),
  },
];

function EntriesPage() {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [globalFilter, setGlobalFilter] = useState("");
  const [journalFilter, setJournalFilter] = useState("");
  // Filtre « écritures produites par cette règle », posé par un lien de la page Règles.
  const [ruleKey, setRuleKey] = useState(() => new URLSearchParams(window.location.search).get("ruleKey") ?? "");
  // Filtre par compte (préfixe) : « 419 » liste tous les 419xxxxx. Posable par
  // l'URL (?account=). Seuls des chiffres sont retenus, saisie comme URL, pour
  // que le champ affiche exactement le critère envoyé.
  const accountPrefix = (raw: string) => raw.replace(/\D/g, "").slice(0, 8);
  const [accountFilter, setAccountFilter] = useState(() => accountPrefix(new URLSearchParams(window.location.search).get("account") ?? ""));
  const { startDate, endDate } = useDateStore();

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    isPending,
  } = useInfiniteQuery({
    queryKey: ["entries", startDate, endDate, journalFilter, globalFilter, ruleKey, accountFilter],
    queryFn: ({ pageParam }) => {
      const params = new URLSearchParams({
        page: String(pageParam),
        perPage: String(PER_PAGE),
        startDate,
        endDate,
        ...(journalFilter && { journal: journalFilter }),
        ...(globalFilter && { search: globalFilter }),
        ...(ruleKey && { ruleKey }),
        ...(accountFilter && { account: accountFilter }),
      });
      return apiGet<EntriesResponse>(`/api/entries?${params}`);
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) =>
      lastPage.entries.length === PER_PAGE ? allPages.length + 1 : undefined,
  });

  const entries = useMemo(
    () => data?.pages.flatMap((p) => p.entries) ?? [],
    [data],
  );

  const table = useReactTable({
    data: entries,
    columns,
    state: { sorting, columnFilters, globalFilter },
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  const { rows } = table.getRowModel();
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const rowVirtualizer = useVirtualizer({
    count: rows.length,
    estimateSize: () => 48,
    getScrollElement: () => tableContainerRef.current,
    overscan: 10,
  });

  const virtualRows = rowVirtualizer.getVirtualItems();
  const totalSize = rowVirtualizer.getTotalSize();
  const paddingTop = virtualRows.length > 0 ? virtualRows[0]?.start || 0 : 0;
  const paddingBottom =
    virtualRows.length > 0
      ? totalSize - (virtualRows[virtualRows.length - 1]?.end || 0)
      : 0;

  useEffect(() => {
    const container = tableContainerRef.current;
    if (!container) return;
    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      if (
        scrollHeight - scrollTop - clientHeight < 500 &&
        hasNextPage &&
        !isFetchingNextPage
      ) {
        void fetchNextPage();
      }
    };
    container.addEventListener("scroll", handleScroll);
    return () => container.removeEventListener("scroll", handleScroll);
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-2xl font-bold text-gray-900">Écritures comptables</h1>
        <span className="text-sm text-gray-500">
          Du {formatDateFr(startDate)} au {formatDateFr(endDate)}
        </span>
      </div>

      <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 flex flex-wrap gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Journal</label>
          <select
            value={journalFilter}
            onChange={(e) => setJournalFilter(e.target.value)}
            className="border border-gray-300 rounded-md px-3 py-2 text-sm"
          >
            <option value="">Tous</option>
            <option value="AN">AN - À-nouveaux</option>
            <option value="VE">VE - Ventes</option>
            <option value="ENC">ENC - Encaissements</option>
            <option value="BQ">BQ - Banque Qonto</option>
            <option value="BC">BC - Banque Crédit Agricole</option>
            <option value="SP">SP - Spiko</option>
            <option value="IB">IB - Interactive Brokers</option>
            <option value="AC">AC - Achats (primes assureurs)</option>
            <option value="PA">PA - Paie</option>
            <option value="OD">OD - Opérations diverses</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Recherche
          </label>
          <input
            type="text"
            value={globalFilter}
            onChange={(e) => setGlobalFilter(e.target.value)}
            placeholder="Email, référence..."
            className="border border-gray-300 rounded-md px-3 py-2 text-sm w-64"
          />
        </div>
        <div>
          <label htmlFor="entries-account" className="block text-sm font-medium text-gray-700 mb-1">
            Compte
          </label>
          <input
            id="entries-account"
            type="text"
            inputMode="numeric"
            value={accountFilter}
            onChange={(e) => setAccountFilter(accountPrefix(e.target.value))}
            placeholder="419, 41900100…"
            title="Préfixe du numéro de compte"
            className="border border-gray-300 rounded-md px-3 py-2 text-sm w-40 font-mono"
          />
        </div>
        {ruleKey && (
          <div className="flex items-end">
            <span className="inline-flex items-center gap-2 rounded bg-blue-50 px-2 py-1.5 text-sm text-blue-800">
              Règle : <span className="font-mono text-xs">{ruleKey}</span>
              <button onClick={() => setRuleKey("")} className="text-xs underline" title="Retirer ce filtre">✕</button>
            </span>
          </div>
        )}
        <div className="flex items-end">
          <span className="text-sm text-gray-500">
            {rows.length} écritures affichées
            {hasNextPage && " (scroll pour charger plus)"}
          </span>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div ref={tableContainerRef} className="overflow-auto max-h-[70vh]">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50 sticky top-0 z-10">
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <th
                      key={header.id}
                      className={`px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase bg-gray-50 ${
                        header.column.getCanSort()
                          ? "cursor-pointer select-none hover:bg-gray-100"
                          : ""
                      }`}
                      onClick={header.column.getToggleSortingHandler()}
                    >
                      <div className="flex items-center gap-1">
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext(),
                            )}
                        {header.column.getIsSorted() && (
                          <span>
                            {header.column.getIsSorted() === "asc" ? " ↑" : " ↓"}
                          </span>
                        )}
                      </div>
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {isPending ? (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center text-gray-500">
                    Chargement...
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center text-gray-500">
                    Aucune écriture trouvée
                  </td>
                </tr>
              ) : (
                <>
                  {paddingTop > 0 && (
                    <tr>
                      <td style={{ height: `${paddingTop}px` }} />
                    </tr>
                  )}
                  {virtualRows.map((virtualRow) => {
                    const row = rows[virtualRow.index];
                    return (
                      <tr
                        key={row.id}
                        className="hover:bg-gray-50"
                        style={{ height: "48px" }}
                      >
                        {row.getVisibleCells().map((cell) => (
                          <td
                            key={cell.id}
                            className="px-4 py-3 whitespace-nowrap text-sm"
                          >
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                  {paddingBottom > 0 && (
                    <tr>
                      <td style={{ height: `${paddingBottom}px` }} />
                    </tr>
                  )}
                </>
              )}
            </tbody>
          </table>
          {(isFetching || isFetchingNextPage) && (
            <div className="py-4 text-center text-sm text-gray-500">Chargement...</div>
          )}
          {!hasNextPage && entries.length > 0 && (
            <div className="py-4 text-center text-sm text-gray-400">
              Fin des écritures
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
